import React, { useEffect, useState } from "react";
import { Box, Grid, CircularProgress, Typography, Grid2, Tabs, Tab, List, ListItemButton, Link, ListItemIcon, ListItemText, ListItem, Collapse, Button } from '@mui/material';
import Badge from './Badge';
import TabPanel from './TabPanel';
import FileRow from "./FileRow";
import { useAppSnackbar } from '../contexts/AppSnackbarContext';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import DescriptionIcon from '@mui/icons-material/Description';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import OutputIcon from '@mui/icons-material/Output';

const DEFAULT_ICON_SIZE = 12;

const WORKFLOW_STATUS = [
    'UNDEFINED',
    'RUNNING',
    'COMPLETED',
    'FAILED',
    'SUSPENDED'
]

const WORKFLOW_STATUS_COLOR = [
    'default',
    'primary',
    'success',
    'error',
    'warning'
]

const WORKFLOW_LOG_TYPES = [
    'INFO',
    'WARNING',
    'ERROR',
    'SUCCESS',
    'DATA',
    'DOCUMENT',
    'MAPPING (in)',
    'MAPPING (out)',
]

class WorkflowDetailDataType {
    static Text = 0;
    static Json = 1;
    static Document = 2;
    static DocumentList = 3;
}

const WORKFLOW_LOG_TYPE_COLOR = [
    'info',
    'warning',
    'error',
    'success',
    'secondary',
    'default',
    'secondary',
    'secondary',
];

class WORKFLOW_LOG_DATA_TYPE {
    static Text = 0;
    static Json = 1;
}

const WORKFLOW_STATUS_ICON = [
    <HelpOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ScheduleIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <CheckCircleOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ErrorOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ScheduleIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />
]

const WORKFLOW_LOG_TYPE_ICON = [
    <ErrorOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ScheduleIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ErrorOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <CheckCircleOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <FormatAlignCenterIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <DescriptionIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ExitToAppIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <OutputIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
];

export default function WorkflowDetail(props) {
    const [loadingCtx, setLoadingCtx] = useState(true);
    const [loadingLogs, setLoadingLogs] = useState(true);
    const [ctx, setCtx] = useState({});
    const [payload, setPayload] = useState({});
    const [logs, setLogs] = useState([]);
    const [groupedLogs, setGroupedLogs] = useState({});
    const [tabValue, setTabValue] = useState(0);
    const [expandState, setExpandState] = useState({});

    const { showSnackbar } = useAppSnackbar();

    useEffect(() => {
        if (props.workflowId) {
            getContext(props.id);
            getLogs(props.id);
        }

    }, [props.workflowId]);

    const getContext = async (id) => {
        try {
            setLoadingCtx(true);
            const response = await fetch(`/api/workflowadmin/${id}`);
            if (response.ok) {
                const result = await response.json();
                setCtx(result);
                setPayload(JSON.parse(result.payload ?? '{}'));
            }
        } catch (error) {
            console.error('Error fetching workflow contexts:', error);
        } finally {
            setLoadingCtx(false);
        }
    }

    const getLogs = async (id) => {
        try {
            setLoadingLogs(true);
            const response = await fetch(`/api/workflowadmin/logs/${id}`);
            if (response.ok) {
                const result = await response.json();
                let allLogs = [];
                result.forEach(r => {
                    allLogs = allLogs.concat(r.logs.map(l => ({ ...l, step: r.key, isSystem: r.isSystem })));
                });
                setLogs(allLogs);
                setGroupedLogs(result);
            }
        } catch (error) {
            console.error('Error fetching workflow contexts:', error);
        } finally {
            setLoadingLogs(false);
        }
    }

    async function postTriggerWorkflow(workflowId, contextId, inputData) {
        try {
            const res = await fetch(`/api/workflow/trigger/${workflowId}/${contextId}`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: inputData
            });
            if (res.ok) {
                showSnackbar("Workflow retrigger succesfully", "success");
                setTimeout(() => {
                    props.refresh && props.refresh();
                }, 1000);
            } else {
                const errorText = await res.text();
                showSnackbar(errorText, "error");
            }
        } catch (err) {
            console.error('Error triggering workflow:', err);
            showSnackbar(err, "error");
        }
    }

    const a11yProps = (index) => {
        return {
            id: `log-tab-${index}-${props.id}`,
            'aria-controls': `log-tabpanel-${index}-${props.id}`,
        };
    }

    function getDuration(logs) {
        if (!logs || logs.length < 2) return "0s";

        // Sort by createdAt ASC
        const sorted = logs.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

        const start = new Date(sorted[0].createdAt);
        const end = new Date(sorted[sorted.length - 1].createdAt);

        let diff = Math.floor((end - start) / 1000); // in seconds

        const hours = Math.floor(diff / 3600);
        diff %= 3600;
        const minutes = Math.floor(diff / 60);
        const seconds = diff % 60;

        let result = [];
        if (hours > 0) result.push(`${hours}h`);
        if (minutes > 0) result.push(`${minutes}m`);
        result.push(`${seconds}s`);

        return result.join(" ");
    }

    const formatDate = (dateString, utc = false) => {
        const date = utc ? new Date(dateString) : new Date(new Date(dateString + 'Z').toLocaleString('en-US'));
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
        const day = date.getDate().toString().padStart(2, '0');
        const yyyymmdd = `${year}-${month}-${day}`;
        return yyyymmdd;
    }

    const formatTime = (dateString, utc = false) => {
        const date = utc ? new Date(dateString) : new Date(new Date(dateString + 'Z').toLocaleString('en-US'));
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        const hhmm = `${hours}:${minutes}:${seconds}`;
        return hhmm;
    }

    const handleTabChange = (_, newValue) => {
        setTabValue(newValue);
    };

    const handleExpandLogClick = (step) => {
        setExpandState(prev => ({ ...prev, [`${props.id}-${step}`]: !prev[`${props.id}-${step}`] }));
    };

    const formatLog = (log, dataType) => {
        if (dataType === WORKFLOW_LOG_DATA_TYPE.Json) {
            try {
                const obj = JSON.parse(log);
                const res = JSON.stringify(obj, null, 4);
                return res;
            } catch (e) {
                console.log('Error parsing JSON log:', e);
                return log;
            }
        }
        return log;
    }

    const handleLogDownload = async (log) => {
        const res = await fetch(`/api/workflowadmin/logFile/${log.id}`);

        if (!res.ok) {
            // handle 401/404/etc
            return;
        }

        const blob = await res.blob();
        // optional: use filename from Content-Disposition if your API sets it
        const fileName = log.log || "file.txt";

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }

    const handleDownload = async (step, type = 'output') => {
        const res = await fetch(`/api/workflowadmin/stepFile/${step.id}/${type}`);

        if (!res.ok) {
            // handle 401/404/etc
            return;
        }

        const blob = await res.blob();
        // optional: use filename from Content-Disposition if your API sets it
        const fileName = type === 'output' ? step.output : step.request;

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }

    const getFileExtension = (filename) => {
        if (typeof filename !== "string") return "";
        const parts = filename.split(".");
        if (parts.length < 2) return "";
        return parts.pop().toLowerCase();
    }

    const handleRetry = () => {
        postTriggerWorkflow(ctx.workflowId, ctx.id, ctx.payload);
    }

    const handleCheckHistory = () => {
        props.refresh && props.refresh(ctx.id);
    }

    return (
        <Box p={2} style={{ height: '600px', overflow: 'auto' }}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Typography variant="h5">
                    Workflow Detail <br />

                    <Button size="small" variant="contained" color="primary" onClick={handleRetry}>Retry</Button>
                    {ctx.wasRetried && (
                        <Button size="small" variant="outlined" sx={{ ml: 2 }} onClick={handleCheckHistory}>Check retry history</Button>
                    )}
                </Typography>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {ctx.lastAttemptStatus > 0 && (
                        <Badge type={WORKFLOW_STATUS_COLOR[ctx.lastAttemptStatus]} icon={WORKFLOW_STATUS_ICON[ctx.lastAttemptStatus]}>
                            {WORKFLOW_STATUS[ctx.lastAttemptStatus]}
                        </Badge>
                    )}
                    <Badge type={WORKFLOW_STATUS_COLOR[ctx.status]} icon={WORKFLOW_STATUS_ICON[ctx.status]}>
                        {WORKFLOW_STATUS[ctx.status]}
                    </Badge>
                </Typography>
            </Box>
            <small>
                {`Context ID: ${ctx.id || ''}`}
            </small>
            <hr />
            {(loadingCtx) && <CircularProgress />}
            {!loadingCtx && (
                <Box>
                    <Grid2 container columns={12} spacing={1}>
                        <Grid2 size={12}>
                            <Typography variant="h6">Payload</Typography>
                            <pre style={{
                                border: '1px solid #ccc',
                                height: '250px',
                                overflow: 'auto',
                            }}>{JSON.stringify(payload, null, 4)}</pre>
                        </Grid2>
                    </Grid2>
                </Box>
            )}
            {loadingLogs && <CircularProgress />}
            {!loadingLogs && (
                <Box mt={4} sx={{ width: '100%' }}>
                    <Typography variant="h6">Logs</Typography>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="log-tabs">
                            <Tab label="Grouped view" {...a11yProps(0)} />
                            <Tab label="Raw logs" {...a11yProps(1)} />
                        </Tabs>
                    </Box>
                    <TabPanel value={tabValue} index={0} id={props.id} sx={{ width: '100%' }}>
                        {groupedLogs?.map(step => (
                            <List key={`${step.id}-${props.id}`} component="nav" aria-labelledby={`step-${step.id}-${props.id}-logs`} sx={{ width: '100%', marginBottom: 0 }} dense disablePadding>
                                <ListItemButton onClick={() => handleExpandLogClick(step.id)} >
                                    <Box flexGrow={1}>
                                        <Typography variant="subtitle1" sx={{ color: step.isSystem ? 'text.secondary' : 'text.primary' }}>
                                            <b>{step.key}</b> ({getDuration(step.logs)})
                                        </Typography>
                                    </Box>
                                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                                        {expandState[`${props.id}-${step.id}`] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </ListItemIcon>
                                </ListItemButton>
                                <Collapse in={expandState[`${props.id}-${step.id}`]} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                                    <List component="div" disablePadding sx={{ width: '100%' }}>
                                        {step.logs.map((log, index) => (
                                            <div key={`${log.id}-log-${index}-${props.id}`}>
                                                <ListItem sx={{ pl: 4, py: 0 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                                        <Badge type={WORKFLOW_LOG_TYPE_COLOR[log.type]} icon={WORKFLOW_LOG_TYPE_ICON[log.type]} width='150px'>
                                                            {WORKFLOW_LOG_TYPES[log.type]}
                                                        </Badge>
                                                        <Badge type='default' width='150px' icon={<ScheduleIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />}>
                                                            {`${formatDate(log.createdAt)} ${formatTime(log.createdAt)}`}
                                                        </Badge>
                                                    </Box>
                                                </ListItem>
                                                <ListItem key={`${step.id}-log-${index}-${props.id}`} sx={{ pl: 4, py: 0, width: '100%' }}>
                                                    {WORKFLOW_LOG_TYPES[log.type] != 'DOCUMENT' && (
                                                        <Box sx={{ width: '100%' }}>
                                                            <pre style={{
                                                                maxHeight: '400px',
                                                                maxWidth: '100%',
                                                                overflow: 'auto',
                                                                whiteSpace: 'pre-wrap'
                                                            }}>
                                                                {formatLog(log.log, log.dataType)}
                                                            </pre>
                                                        </Box>
                                                    )}
                                                </ListItem>
                                            </div>
                                        ))}
                                        {!step.isSystem && (
                                            <ListItem>
                                                <Box width='100%'>
                                                    <Grid2 container columns={12} spacing={1}>
                                                        <Grid2 size={6}>
                                                            <Typography variant="h6">Request</Typography>
                                                            {step.requestType == WorkflowDetailDataType.Text && (
                                                                <>
                                                                    {step.request}
                                                                </>
                                                            )}
                                                            {step.requestType == WorkflowDetailDataType.Json && (
                                                                <pre style={{
                                                                    border: '1px solid #ccc',
                                                                    height: '250px',
                                                                    overflow: 'auto',
                                                                }}>{formatLog(step.request, 1)}</pre>
                                                            )}
                                                            {step.requestType == WorkflowDetailDataType.Document && (
                                                                <Box>
                                                                    <FileRow name={step.request} ext={getFileExtension(step.request)} onDownload={() => handleDownload(step, 'request')} />
                                                                </Box>
                                                            )}
                                                        </Grid2>
                                                        <Grid2 size={6}>
                                                            <Typography variant="h6">Output</Typography>
                                                            {step.outputType == WorkflowDetailDataType.Text && (
                                                                <>
                                                                    {step.output}
                                                                </>
                                                            )}
                                                            {step.outputType == WorkflowDetailDataType.Json && (
                                                                <pre style={{
                                                                    border: '1px solid #ccc',
                                                                    height: '250px',
                                                                    overflow: 'auto',
                                                                }}>{formatLog(step.output, 1)}</pre>
                                                            )}
                                                            {step.outputType == WorkflowDetailDataType.Document && (
                                                                <Box>
                                                                    <FileRow name={step.output} ext={getFileExtension(step.output)} onDownload={() => handleDownload(step)} />
                                                                </Box>
                                                            )}
                                                        </Grid2>
                                                    </Grid2>
                                                </Box>
                                            </ListItem>
                                        )}
                                    </List>
                                </Collapse>
                            </List>
                        ))}
                    </TabPanel>
                    <TabPanel value={tabValue} index={1} id={props.id}>
                        <small>Time is displayed in UTC</small>
                        {logs.map(log =>
                            <Grid2 container key={`raw-log-${log.id}-${props.id}`} spacing={1} columns={13}>
                                <Grid2 size={2}>
                                    <pre style={{
                                        color: log.isSystem ? 'gray' : 'black',
                                        overflow: 'auto',
                                        width: '100%',
                                        whiteSpace: 'pre-wrap'
                                    }}>
                                        {`[${formatDate(log.createdAt, true)}T${formatTime(log.createdAt, true)}]`}
                                    </pre>
                                </Grid2>
                                <Grid2 size={3}>
                                    <pre style={{
                                        color: log.isSystem ? 'gray' : 'black',
                                        overflow: 'auto',
                                        width: '100%',
                                        whiteSpace: 'pre-wrap'
                                    }}>
                                        [{log.step}]
                                        [{WORKFLOW_LOG_TYPES[log.type]}]
                                    </pre>
                                </Grid2>
                                <Grid2 size={8}>
                                    {WORKFLOW_LOG_TYPES[log.type] == 'DOCUMENT' && (
                                        <pre style={{
                                            maxHeight: '250px',
                                            width: '100%',
                                            overflow: 'auto',
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={() => handleLogDownload(log)}
                                            >
                                                {log.log}
                                            </Link>
                                        </pre>
                                    )}
                                    {WORKFLOW_LOG_TYPES[log.type] != 'DOCUMENT' && (
                                        <pre style={{
                                            color: log.isSystem ? 'gray' : 'black',
                                            maxHeight: '250px',
                                            width: '100%',
                                            overflow: 'auto',
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            <span>{formatLog(log.log, log.dataType)}</span>
                                        </pre>
                                    )}
                                </Grid2>
                            </Grid2>
                        )}
                    </TabPanel>
                </Box >
            )
            }
        </Box >
    );
}
